// src/lib/auth/app-context.ts
'use client';

export interface UserType {
  id?: number;
  accessSuffix?: string | null;
  nickname: string;
  oauthId: string;
  email?: string;
  bio?: string;
  crystals?: number;
  playerId?: string;
  hasAccess?: boolean;
  role?: string;
  roles?: Array<{ id: number; role: string }>;
  isBanned?: boolean;
  bannedUntil?: string | null;
  banReason?: string | null;
  warningRestrictionUntil?: string | null;
  mcNickname?: string;
  mcBedrock?: string;
}

export interface RegionData {
  countryCode: string;
  currency: string;
  isAdmin: boolean;
  rules: any[];
}

export type AuthStatus = 'REGISTERED' | 'NEEDS_REGISTRATION' | 'GUEST';

export interface AppContext {
  user: UserType | null;
  status: AuthStatus;
  regionData: RegionData | null;
  lang: string;
  fetchedAt: number;
}

export const LOCAL_API_URL =
  process.env.NEXT_PUBLIC_LOCAL_API;
export const IDP_API_URL = process.env.NEXT_PUBLIC_API_BASE;
export const CLIENT_ID = 'russia';
export const REDIRECT_URI = 'https://rplands.royolands.com';

const ME_TTL_MS = 30_000;
const REGION_TTL_MS = 5 * 60_000;

// ---------------------------------------------------------------
// Rehydration store (аналог bX / JT в TikTok)
// ---------------------------------------------------------------
let appContext: AppContext | null = null;

export function getCachedAppContext(): AppContext | null {
  return appContext;
}

export function setCachedAppContext(ctx: AppContext) {
  appContext = ctx;
}

export function invalidateAppContext() {
  appContext = null;
  meInflight = null;
  meCache = null;
  regionInflight = null;
  regionCache = null;
}

// ---------------------------------------------------------------
// In-flight dedupe + TTL (аналог o ||= fetch(...))
// ---------------------------------------------------------------
let meInflight: Promise<any> | null = null;
let meCache: { data: any; ts: number } | null = null;

let regionInflight: Promise<RegionData | null> | null = null;
let regionCache: { data: RegionData | null; ts: number } | null = null;

export async function fetchMe(force = false): Promise<any> {
  if (!force && meCache && Date.now() - meCache.ts < ME_TTL_MS) {
    return meCache.data;
  }
  if (meInflight) return meInflight;

  meInflight = (async () => {
    try {
      const res = await fetch(`${LOCAL_API_URL}/auth/me`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!res.ok) {
        meCache = { data: null, ts: Date.now() };
        return null;
      }
      const data = await res.json();
      meCache = { data, ts: Date.now() };
      return data;
    } catch {
      meCache = { data: null, ts: Date.now() };
      return null;
    } finally {
      meInflight = null;
    }
  })();
  return meInflight;
}

export async function fetchRegion(force = false): Promise<RegionData | null> {
  if (!force && regionCache && Date.now() - regionCache.ts < REGION_TTL_MS) {
    return regionCache.data;
  }
  if (regionInflight) return regionInflight;

  regionInflight = (async () => {
    try {
      const res = await fetch(`${LOCAL_API_URL}/region`, {
        credentials: 'include',
      });
      if (!res.ok) {
        regionCache = { data: null, ts: Date.now() };
        return null;
      }
      const data = (await res.json()) as RegionData;
      regionCache = { data, ts: Date.now() };
      return data;
    } catch {
      regionCache = { data: null, ts: Date.now() };
      return null;
    } finally {
      regionInflight = null;
    }
  })();
  return regionInflight;
}

// ---------------------------------------------------------------
// Главный bootstrap (аналог Bi / fetchBaseAppContext в TikTok)
// ---------------------------------------------------------------
let inflight: Promise<AppContext> | null = null;

export async function fetchAppContext(
  lang: string,
  force = false
): Promise<AppContext> {
  if (!force && appContext) return appContext;
  if (inflight) return inflight;

  inflight = (async () => {
    const [me, region] = await Promise.all([fetchMe(force), fetchRegion(force)]);
    const ctx: AppContext = {
      user: me?.user ?? null,
      status: (me?.status as AuthStatus) ?? 'GUEST',
      regionData: region ?? null,
      lang,
      fetchedAt: Date.now(),
    };
    appContext = ctx;
    return ctx;
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

// ---------------------------------------------------------------
// SSO probe (аналог check-cross-domain у вас, PKCE-обмен)
// ---------------------------------------------------------------
export async function checkGlobalSSO(): Promise<boolean> {
  try {
    const fetchSSO = async () => {
      const res = await fetch(
        `${IDP_API_URL}/auth/check-cross-domain?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`,
        { credentials: 'include', cache: 'no-store' }
      );
      if (!res.ok) throw new Error('Bad SSO response');
      return await res.json();
    };

    let data = await fetchSSO();
    if (!data.authenticated) data = await fetchSSO();

    if (data.authenticated && data.auth_code && data.code_verifier) {
      try {
        const loginRes = await fetch(`${LOCAL_API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: data.auth_code,
            code_verifier: data.code_verifier,
          }),
          credentials: 'include',
        });
        if (loginRes.ok) {
          // обновили cookie → сбрасываем кэш, следующий fetchMe даст нового user
          meCache = null;
          meInflight = null;
          appContext = null;
          return true;
        }
      } catch (e) {
        console.warn('SSO login request failed', e);
      }
    }

    return false;
  } catch {
    return false;
  }
}